<template>
  <div>
    <Head :title="title" />
    <div class="bg-white rounded-md shadow overflow-hidden max-w-3xl">
      <form @submit.prevent="update">
        <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
          
          <!-- Status Name -->
          <text-input 
            v-model="form.name" 
            :error="form.errors.name" 
            class="pr-6 pb-8 w-full lg:w-full" 
            :label="__('Name')" 
            required
            @input="generateSlug"
          />

          <!-- Auto Generated Slug (Read Only) -->
          <div class="pr-6 pb-8 w-full">
            <label class="form-label">{{ __('Slug') }} ({{ __('Auto Generated') }})</label>
            <input
              type="text"
              v-model="form.slug"
              readonly
              class="form-input w-full bg-gray-100 cursor-not-allowed"
              placeholder="Auto generated from name..."
            />
            <p class="text-xs text-gray-500 mt-1">{{ __('This will be automatically updated when you change the name') }}</p>
            <div v-if="form.errors.slug" class="form-error mt-1 text-sm text-red-600">
              {{ form.errors.slug }}
            </div>
          </div>

          <!-- Departments Selection -->
          <div class="pr-6 pb-8 w-full">
            <label class="form-label">{{ __('Departments') }}</label>
            <div class="mt-2 space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
              <div
                v-for="department in departments"
                :key="department.id"
                class="flex items-center"
              >
                <input
                  :id="`dept-${department.id}`"
                  type="checkbox"
                  :value="department.id"
                  v-model="form.departments"
                  class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  :for="`dept-${department.id}`"
                  class="ml-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900"
                >
                  {{ department.name }}
                </label>
              </div>
            </div>
            <div v-if="form.errors.departments" class="form-error mt-1 text-sm text-red-600">
              {{ form.errors.departments }}
            </div>
          </div>

          <!-- Selected Departments Preview -->
          <div v-if="selectedDepartmentNames.length > 0" class="pr-6 pb-8 w-full">
            <div class="bg-indigo-50 p-4 rounded-md border border-indigo-200">
              <p class="text-sm font-medium text-indigo-700 mb-2">
                {{ __('Selected Departments') }} ({{ selectedDepartmentNames.length }}):
              </p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="name in selectedDepartmentNames"
                  :key="name"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                >
                  {{ name }}
                </span>
              </div>
            </div>
          </div>
          
        </div>

        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
          <button
            v-if="!status.deleted_at"
            class="text-red-600 hover:underline"
            tabindex="-1"
            type="button"
            @click="destroy"
          >
            {{ __('Delete Status') }}
          </button>
          <button
            v-else
            class="text-indigo-600 hover:underline"
            tabindex="-1"
            type="button"
            @click="restore"
          >
            {{ __('Restore Status') }}
          </button>
          <loading-button 
            :loading="form.processing" 
            class="btn-indigo" 
            type="submit"
          >
            {{ __('Update') }} {{ __('Status') }}
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Link, Head } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  metaInfo() {
    return { title: this.status.name }
  },
  components: {
    LoadingButton,
    TextInput,
    Link,
    Head,
  },
  layout: Layout,
  props: {
    title: String,
    status: Object,
    departments: {
      type: Array,
      default: () => []
    }
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        name: this.status.name,
        slug: this.status.slug,
        departments: this.status.departments || []
      }),
    }
  },
  computed: {
    selectedDepartmentNames() {
      if (!this.departments || !Array.isArray(this.departments)) {
        return []
      }
      return this.departments
        .filter(dept => this.form.departments.includes(dept.id))
        .map(dept => dept.name)
    }
  },
  methods: {
    generateSlug() {
      if (this.form.name) {
        // Convert to lowercase, replace spaces and special chars with underscore
        this.form.slug = this.form.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
      } else {
        this.form.slug = ''
      }
    },
    update() {
      this.form.put(this.route('statuses.update', this.status.id))
    },
    destroy() {
      if (confirm('Are you sure you want to delete this status?')) {
        this.$inertia.delete(this.route('statuses.destroy', this.status.id))
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this status?')) {
        this.$inertia.put(this.route('statuses.restore', this.status.id))
      }
    },
  },
}
</script>